/**
 * Burst Air Control — Smash-style DI during tumble + velocity steering after tumble.
 *
 * Two layers of post-burst trajectory control:
 * 1. Tumble DI: subtle velocity nudge during hitstun (like Smash DI)
 * 2. Air control: velocity-based steering after tumble exits (like Smash aerial drift)
 *
 * Both modify vx/vy deterministically based on input — no new sync messages needed.
 * Input relayed via key_state at 60Hz, minor velocity divergence corrected by turn_snapshot.
 */

import { Keys } from 'bonkjs';
import { BURST } from '../constants';
import type { Player } from '../types';
import type { InputProvider } from '../input';

/**
 * Tumble DI — directional velocity influence during burst hitstun.
 *
 * Gate: player.tumble && player.burstFlying && player.hitstunFrames > 0
 *
 * Over max hitstun (75 frames), max horizontal influence = 75 × 0.05 = 3.75 velocity units
 * (~47% of the 8.0 burst impulse — meaningful but can't fully cancel).
 * Vertical DI is weaker (0.5× multiplier) to prevent trivially canceling gravity.
 */
export function applyBurstTumbleDI(player: Player, input: InputProvider): void {
  if (!player.tumble || !player.burstFlying || player.hitstunFrames <= 0) return;

  let inputX = 0;
  let inputY = 0;
  if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) inputX -= 1;
  if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) inputX += 1;
  if (input.getKey(Keys.ArrowUp) || input.getKey(Keys.W)) inputY -= 1;
  if (input.getKey(Keys.ArrowDown) || input.getKey(Keys.S)) inputY += 1;

  if (inputX !== 0) {
    player.vx += inputX * BURST.TUMBLE_DI_ACCEL;
  }
  if (inputY !== 0) {
    player.vy += inputY * BURST.TUMBLE_DI_ACCEL * BURST.TUMBLE_DI_VERTICAL_MULT;
  }
}

/**
 * Post-burst air control — velocity-based steering after tumble exits.
 *
 * Gate: !player.tumble && player.burstFlying && !player.grounded && !player.liftActive
 *
 * Opposing bonus (1.5×): when input opposes current vx direction, acceleration is stronger —
 * rewards fighting momentum to recover toward safety.
 *
 * No momentum cost (burst already paid its cost at resolve).
 * Runs alongside existing position-based teleport steering (teleport = instant visual feedback,
 * velocity = real trajectory change).
 */
export function applyBurstAirControl(player: Player, moveDir: number): void {
  if (player.tumble || !player.burstFlying || player.grounded || player.liftActive) return;
  if (moveDir === 0) return;

  // Opposing bonus: stronger accel when fighting current velocity direction
  const opposing = (moveDir > 0 && player.vx < 0) || (moveDir < 0 && player.vx > 0);
  const accel = opposing
    ? BURST.AIR_CONTROL_ACCEL * BURST.AIR_CONTROL_OPPOSING_MULT
    : BURST.AIR_CONTROL_ACCEL;

  player.vx += moveDir * accel;
}
